import { formatString } from "@/helpers/formatting";

import { axiosClient } from "../../client";
import endpoints from "../../endpoints";

import type { Entity } from "@/enums/entityProfiles";
import type { IProfile } from "@/models/businesses";
import type {
  BusinessProfileRelationship,
  CreateEntitySnapshotPayload,
  IndividualProfileRelationship,
  OwnershipSnapshot,
  RelationshipPayload,
} from "@/models/entityProfiles";

class RelationsApiService {
  async getRelations<T extends Entity>(type: T, id: string) {
    const url = formatString(endpoints.PROFILES.RELATIONS, type, id);
    const response = await axiosClient.get<{
      data: T extends Entity.individual
        ? IndividualProfileRelationship
        : BusinessProfileRelationship;
    }>(url);
    return response.data.data;
  }

  async getLatestEntityOwnershipSnapshot(
    entityId: IProfile["id"],
    entityType: Entity
  ) {
    const response = await axiosClient.get<
      { data: OwnershipSnapshot } | undefined
    >(
      formatString(
        endpoints.PROFILES.LATEST_ENTITY_OWNERSHIP_SNAPSHOT,
        entityType,
        entityId
      )
    );
    return response.data?.data;
  }

  async createEntityOwnershipSnapshot(
    entityId: IProfile["id"],
    entityType: Entity,
    data: CreateEntitySnapshotPayload
  ) {
    const response = await axiosClient.post<{ data: OwnershipSnapshot }>(
      formatString(
        endpoints.PROFILES.ENTITY_OWNERSHIP_SNAPSHOT,
        entityType,
        entityId
      ),
      data
    );
    return response.data.data;
  }

  async createRelationships<T extends Entity>(
    profileId: string,
    initiatingEntityType: T,
    relations: RelationshipPayload
  ) {
    const url = formatString(
      endpoints.PROFILES.RELATIONS,
      initiatingEntityType,
      profileId
    );
    const response = await axiosClient.post<{
      data: T extends Entity.individual
        ? IndividualProfileRelationship
        : BusinessProfileRelationship;
    }>(url, relations);
    return response.data.data;
  }

  async removeRelationships(profileId: string, relations: RelationshipPayload) {
    const url = formatString(
      endpoints.PROFILES.RELATIONS,
      "business",
      profileId
    );
    await axiosClient.delete(url, { data: relations });
  }
}

const relationsApiService = new RelationsApiService();
export default relationsApiService;
